import {
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  LightMode,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { ProductApproval } from '@texas/api/endpoints/productApprovalsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { usePublisher } from '@texas/messageBus/useMessageBus';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Slot } from './Slot';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { LinkOptionModal } from './LinkOptionModal';
import { angularEvents } from '@bridge/angularEvents';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { ConfigureCompositionGroupGarp } from '@texas/messageBus/messages';
import { HoverCard } from '@texas/components/shared/HoverCard';
import { SampleState as SampleStateComponent } from '@texas/components/shared/samples/SampleState';
import { UserBadge } from '@texas/components/shared/users/UserBadge';

export function GarpVariantOverview({
  articleId,
  variantId,
  branchId,
}: {
  articleId: number;
  variantId: number;
  branchId: number;
}) {
  const { t } = useTranslation();
  const { publish } = usePublisher();
  const {
    isOpen: isOptionModalOpen,
    onOpen: onOptionModalOpen,
    onClose: onOptionModalClose,
  } = useDisclosure();

  const [_versionData, _setVersionData] = useState<{
    versionId: number;
    paId?: number;
  } | null>(null);

  const {
    data: branchOverview,
    refetch: refetchBranchOverview,
    loading: _branchOverviewLoading,
  } = useApiResource(variantsApi.getBranchOverview);

  const { data: variantInfo, refetch: refetchVariantInfo } = useApiResource(
    variantsApi.getVariantInfo,
  );

  useEffect(() => {
    refetchBranchOverview(variantId, branchId);
    refetchVariantInfo(variantId);
  }, [
    articleId,
    branchId,
    variantId,
    refetchBranchOverview,
    refetchVariantInfo,
  ]);

  const handleMapGarpFromOption = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (
      branchOverview?.linkedOption?.id &&
      branchOverview.productDevelopmentVariantId
    ) {
      publish(
        new ConfigureCompositionGroupGarp(
          branchOverview.linkedOption.id,
          branchOverview.linkedOption.linkedVariantId!,
          branchId,
        ),
      );
    }
  };

  const handleMapGarpFromSpecificPA = (pa: ProductApproval) => {
    if (branchOverview?.linkedOption?.linkedVariantId) {
      publish(
        new ConfigureCompositionGroupGarp(
          pa.compositionGroupId,
          branchOverview.linkedOption.linkedVariantId,
          branchId,
          pa.id,
        ),
      );
    }
  };

  return (
    <LightMode>
      <Box w="full">
        <Flex gap={4} alignItems="center" mb={4}>
          {branchOverview && (
            <>
              <Slot
                icon={<Icons.Factory color="black" />}
                fulfilled={!!branchOverview.mainPurchaseInfo?.supplierId}
              >
                <Flex flexDir="column" gap={0}>
                  <Text variant="sub" fontSize="xs">
                    {t('garp.mainSupplier')}
                  </Text>
                  <Heading fontSize="md">
                    {branchOverview.mainPurchaseInfo?.supplierId ? (
                      <>
                        {branchOverview.mainPurchaseInfo.supplierName} (
                        {branchOverview.mainPurchaseInfo.supplierNumber})
                      </>
                    ) : (
                      <>{t('garp.noSupplier')}</>
                    )}
                  </Heading>
                </Flex>
              </Slot>
              <WarningComponent
                warning={
                  branchOverview.linkedOption
                    ? undefined
                    : t('garp.noLinkedOption')
                }
              >
                <Slot
                  icon={<Icons.PencilRuler color="black" />}
                  fulfilled={!!branchOverview.linkedOption}
                  onClick={onOptionModalOpen}
                >
                  <Flex flexDir="column" gap={0} flex={1}>
                    <Text variant="sub" fontSize="xs">
                      {t('garp.linkedOption')}
                    </Text>
                    <Heading fontSize="md">
                      {branchOverview.linkedOption ? (
                        <CompositionGroupTitle
                          version={branchOverview.linkedOption.version}
                          note={branchOverview.linkedOption.note}
                          type="option"
                        />
                      ) : (
                        <>{t('garp.noLinkedOption')}</>
                      )}
                    </Heading>
                  </Flex>
                  {branchOverview.linkedOption &&
                    !branchOverview.isMatrixArticle && (
                      <Tooltip label={t('garp.fillData')}>
                        <IconButton
                          aria-label={t('garp.fillData')}
                          icon={
                            <Icons.BreifcaseArrowLeftRight
                              color="black"
                              boxSize={defaultIconSize}
                            />
                          }
                          size="sm"
                          onClick={handleMapGarpFromOption}
                        />
                      </Tooltip>
                    )}
                </Slot>
              </WarningComponent>

              <HoverCard
                content={
                  branchOverview.linkedOption?.linkedVariantId && (
                    <ProductApprovalList
                      pas={branchOverview.productApprovals}
                      variantId={branchOverview.linkedOption.linkedVariantId}
                      variantName={variantInfo?.name ?? ''}
                      branchId={branchId}
                      isMatrixArticle={branchOverview.isMatrixArticle}
                      onMapGarp={handleMapGarpFromSpecificPA}
                    />
                  )
                }
              >
                <Box>
                  <Slot
                    icon={<Icons.Eye color="black" />}
                    fulfilled={branchOverview.acceptedProductApprovalsCount > 0}
                  >
                    <Flex flexDir="column" gap={0} flex={1}>
                      <Text variant="sub" fontSize="xs">
                        {t('garp.productApproval')}
                      </Text>
                      <Heading fontSize="md">
                        {branchOverview.acceptedProductApprovalsCount > 0
                          ? t('garp.acceptedPa', {
                              count:
                                branchOverview.acceptedProductApprovalsCount,
                            })
                          : branchOverview.pendingProductApprovalsCount > 0
                          ? t('garp.pendingPa', {
                              count:
                                branchOverview.pendingProductApprovalsCount,
                            })
                          : t('garp.noProductApprovals')}
                      </Heading>
                    </Flex>
                  </Slot>
                </Box>
              </HoverCard>
              <Slot
                icon={<Icons.CashMinus color="black" />}
                fulfilled={
                  !!branchOverview.mainPurchaseInfo &&
                  branchOverview.mainPurchaseInfo.isReady
                }
              >
                <Flex flexDir="column" gap={0}>
                  <Text variant="sub" fontSize="xs">
                    {t('garp.purchaseInformation')}
                  </Text>
                  <Heading fontSize="md">
                    {branchOverview.mainPurchaseInfo?.isReady
                      ? t('general.isReady')
                      : t('general.isNotReady')}
                  </Heading>
                </Flex>
              </Slot>
              <Slot
                icon={<Icons.CashPlus color="black" />}
                fulfilled={
                  !!branchOverview.salesInfo && branchOverview.salesInfo.isReady
                }
              >
                <Flex flexDir="column" gap={0}>
                  <Text variant="sub" fontSize="xs">
                    {t('garp.salesInformation')}
                  </Text>
                  <Heading fontSize="md">
                    {branchOverview.salesInfo?.isReady
                      ? t('general.isReady')
                      : t('general.isNotReady')}
                  </Heading>
                </Flex>
              </Slot>
            </>
          )}
        </Flex>
        <Divider mb={4} />
        {branchOverview?.productDevelopmentVariantId && (
          <>
            <LinkOptionModal
              isOpen={isOptionModalOpen}
              onClose={onOptionModalClose}
              variantId={variantId}
              variantName={variantInfo?.name}
              productDevelopmentVariantId={
                branchOverview.productDevelopmentVariantId
              }
              currentlyLinkedOptionId={branchOverview.linkedOption?.id}
              onLinked={() => {
                refetchBranchOverview(variantId, branchId);
                angularEvents.articleManagementUpdated.dispatch();
              }}
            />
          </>
        )}
      </Box>
    </LightMode>
  );
}

function ProductApprovalList({
  pas,
  isMatrixArticle,
  onMapGarp,
}: {
  pas: ProductApproval[];
  variantId: number;
  variantName: string;
  branchId: number;
  isMatrixArticle: boolean;
  onMapGarp: (pa: ProductApproval) => void;
}) {
  const { t } = useTranslation();
  if (pas.length === 0) {
    return <Text fontSize="sm">{t('garp.noProductApprovals')}</Text>;
  }

  return (
    <Flex flexDir="column" gap={2} maxW="400px">
      {pas.map((pa) => (
        <Box
          key={pa.id}
          p={2}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          bg="white"
        >
          <Flex justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Flex flexDir="column" gap={0}>
              <Text fontSize="sm" fontWeight="semibold">
                {pa.formatNumber}
              </Text>
              {isMatrixArticle && (pa.matrixNodeXValue || pa.matrixNodeYValue) && (
                <Flex gap={1} mt={0.5}>
                  {pa.matrixNodeXValue && (
                    <Text fontSize="xs" color="gray.500">
                      {pa.matrixNodeXValue}/
                    </Text>
                  )}
                  {pa.matrixNodeYValue && (
                    <Text fontSize="xs" color="gray.500">
                      {pa.matrixNodeYValue}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
            <Tooltip label={t('garp.fillData')}>
              <IconButton
                icon={<Icons.BreifcaseArrowLeftRight />}
                aria-label={t('garp.fillData')}
                size="xs"
                variant="ghost"
                onClick={() => onMapGarp(pa)}
              />
            </Tooltip>
          </Flex>
          {pa.sample ? (
            <Flex alignItems="center" gap={2} mt={1}>
              <UserBadge
                name={pa.sample.inPossessionOfUser.name}
                userId={pa.sample.inPossessionOfUser.id}
                branch={pa.sample.inPossessionOfUser.branchIdentifier}
                boxSize={6}
              />
              <Text fontSize="xs" color="gray.600">
                {t('garp.sampleVersion', {
                  version: pa.compositionGroupVersion,
                })}
              </Text>
              <SampleStateComponent
                lifecycle={pa.sample.lifecycle}
                sampleReadyDate={pa.sample.sampleReadyDate}
              />
            </Flex>
          ) : (
            <Text fontSize="xs" color="gray.500" mt={1}>
              {t('garp.noSample')}
            </Text>
          )}
        </Box>
      ))}
    </Flex>
  );
}
